import { AUTH_USER_TOKEN_KEY, AUTH_USER_TOKEN_KEY_LEGACY, AUTH_ACCESS_TOKEN_KEY, AUTH_REFRESH_TOKEN_KEY } from "./constants";

// Migrate tokens from legacy Amplify key to current key (one-time, on load)
try {
  const legacyToken = window.localStorage.getItem(AUTH_USER_TOKEN_KEY_LEGACY);
  if (legacyToken && legacyToken !== "undefined" && legacyToken !== "null") {
    if (!window.localStorage.getItem(AUTH_USER_TOKEN_KEY)) {
      window.localStorage.setItem(AUTH_USER_TOKEN_KEY, legacyToken);
    }
    window.localStorage.removeItem(AUTH_USER_TOKEN_KEY_LEGACY);
  }
} catch (_e) {
  // ignore
}

export const parseJwtPayload = (token) => {
  try {
    const payloadPart = String(token || "").split(".")[1];
    if (!payloadPart) return null;
    const padded = payloadPart
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(payloadPart.length + ((4 - (payloadPart.length % 4)) % 4), "=");
    return JSON.parse(window.atob(padded));
  } catch (_error) {
    return null;
  }
};

const getTokenEmail = (payload) => {
  if (!payload) return null;
  return (
    payload.email ||
    payload.preferred_username ||
    payload.upn ||
    payload.unique_name ||
    null
  );
};

const getTokenUserId = (payload) => {
  if (!payload) return null;
  return (
    payload["cognito:username"] ||
    payload.sub ||
    payload.oid ||
    getTokenEmail(payload) ||
    payload.username ||
    null
  );
};

export const getStoredAuthToken = () => {
  try {
    return window.localStorage.getItem(AUTH_USER_TOKEN_KEY);
  } catch (_error) {
    return null;
  }
};

export const hasStoredAuthToken = () => {
  const token = getStoredAuthToken();
  return Boolean(token && token !== "undefined" && token !== "null");
};

export const getAuthEmailFromToken = () => {
  const token = getStoredAuthToken();
  if (!token || token === "undefined" || token === "null") return null;
  const payload = parseJwtPayload(token);
  if (!payload) return null;
  return getTokenEmail(payload);
};

export const getAuthUserIdFromToken = () => {
  const token = getStoredAuthToken();
  if (!token || token === "undefined" || token === "null") return null;
  const payload = parseJwtPayload(token);
  if (!payload) return null;
  return getTokenUserId(payload);
};

export const getAuthProfileFromToken = () => {
  const token = getStoredAuthToken();
  if (!token || token === "undefined" || token === "null") return null;
  const payload = parseJwtPayload(token);
  if (!payload) return null;
  return {
    email: getTokenEmail(payload),
    preferredUsername: payload.preferred_username || payload.upn || null,
    name: payload.name || payload.unique_name || null,
    givenName: payload.given_name || null,
    familyName: payload.family_name || null,
    userId: getTokenUserId(payload),
  };
};

export const isTokenExpired = (bufferSeconds = 60) => {
  const token = getStoredAuthToken();
  if (!token || token === "undefined" || token === "null") return true;
  const payload = parseJwtPayload(token);
  if (!payload?.exp) return false;
  const nowSeconds = Math.floor(Date.now() / 1000);
  return payload.exp - nowSeconds < bufferSeconds;
};

export const getStoredRefreshToken = () => {
  try {
    return window.localStorage.getItem(AUTH_REFRESH_TOKEN_KEY);
  } catch (_error) {
    return null;
  }
};

export const refreshAuthToken = async () => {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) return null;

  try {
    const cognitoDomain = process.env.REACT_APP_COGNITO_OAUTH_DOMAIN;
    const clientId = process.env.REACT_APP_COGNITO_USER_POOL_CLIENT_ID;
    if (!cognitoDomain || !clientId) return null;

    const body = new URLSearchParams();
    body.set("grant_type", "refresh_token");
    body.set("client_id", clientId);
    body.set("refresh_token", refreshToken);

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 8000);

    const response = await fetch(`https://${cognitoDomain}/oauth2/token`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
      signal: controller.signal,
    }).finally(() => window.clearTimeout(timeoutId));

    if (!response.ok) return null;

    const data = await response.json();
    if (!data.id_token) return null;

    window.localStorage.setItem(AUTH_USER_TOKEN_KEY, data.id_token);
    if (data.access_token) {
      window.localStorage.setItem(AUTH_ACCESS_TOKEN_KEY, data.access_token);
    }
    return data.id_token;
  } catch (_error) {
    return null;
  }
};

let _refreshPromise = null;
const withTimeout = (promise, timeoutMs) =>
  Promise.race([
    promise,
    new Promise((resolve) => {
      window.setTimeout(() => resolve(null), timeoutMs);
    }),
  ]);

export const getValidToken = async () => {
  let token = getStoredAuthToken();
  if (token && isTokenExpired(120)) {
    if (!_refreshPromise) {
      _refreshPromise = withTimeout(refreshAuthToken(), 9000).finally(() => {
        _refreshPromise = null;
      });
    }
    const newToken = await _refreshPromise;
    if (newToken) token = newToken;
  }
  return token || null;
};

export const clearAuthStorage = () => {
  try {
    window.localStorage.removeItem(AUTH_USER_TOKEN_KEY);
    window.localStorage.removeItem(AUTH_USER_TOKEN_KEY_LEGACY);
    window.localStorage.removeItem(AUTH_REFRESH_TOKEN_KEY);
    window.localStorage.removeItem(AUTH_ACCESS_TOKEN_KEY);
    window.localStorage.removeItem("LOGIN_ACTION");
  } catch (_error) {
    // best effort only
  }
};

const ALLOWED_REDIRECT_DOMAINS = ["yourrighthand.ai", "oelement.ai", "oelement.net", "localhost"];

const normalizeHost = (value) => {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .split("/")[0]
    .replace(/^www\./, "")
    .split(":")[0];
};

const isLocalHost = (value) => {
  const host = normalizeHost(value);
  return host === "localhost" || host === "127.0.0.1";
};

export const isAllowedRedirectHost = (host) => {
  const h = normalizeHost(host);
  if (!h) return false;
  return ALLOWED_REDIRECT_DOMAINS.some((d) => h === d || h.endsWith(`.${d}`));
};

const getCurrentHost = () => {
  const hostname = window.location.hostname;
  return hostname === "localhost" || hostname === "127.0.0.1"
    ? window.location.host
    : hostname;
};

export const resolveLoginUrl = () => {
  const loginBase = process.env.REACT_APP_LOGIN_URL;
  if (!loginBase) return null;
  const loginUrl = new URL(`${loginBase}/`);
  const currentHost = getCurrentHost();
  if (isAllowedRedirectHost(currentHost)) {
    loginUrl.searchParams.set("from", currentHost);
  }
  return loginUrl.toString();
};

export const signOutAndRedirect = async () => {
  const refreshToken = getStoredRefreshToken();
  if (refreshToken) {
    try {
      const cognitoDomain = process.env.REACT_APP_COGNITO_OAUTH_DOMAIN;
      const clientId = process.env.REACT_APP_COGNITO_USER_POOL_CLIENT_ID;
      if (cognitoDomain && clientId) {
        const body = new URLSearchParams();
        body.set("token", refreshToken);
        body.set("client_id", clientId);
        await fetch(`https://${cognitoDomain}/oauth2/revoke`, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: body.toString(),
        });
      }
    } catch (_error) {
      // best effort
    }
  }
  clearAuthStorage();
  const loginBase = process.env.REACT_APP_LOGIN_URL;
  if (!loginBase) {
    window.location.reload();
    return;
  }
  const logoutUrl = new URL(`${loginBase}/logout`);
  logoutUrl.searchParams.set("logout", "1");
  const currentHost = getCurrentHost();
  if (isAllowedRedirectHost(currentHost)) {
    logoutUrl.searchParams.set("from", currentHost);
  }
  window.location.assign(logoutUrl.toString());
};
